import React from "react";
import { useAuth } from "@/lib/auth";
import { api, setStoredToken } from "@/lib/api";
import LinkedInLabBrand from "@/components/LinkedInLabBrand";
import Owlbert from "@/components/Owlbert";

export default function Login() {
  const { user, loading, checkAuth } = useAuth();
  const [mode, setMode] = React.useState("login");
  const [email, setEmail] = React.useState("");
  const [password, setPassword] = React.useState("");
  const [name, setName] = React.useState("");
  const [err, setErr] = React.useState("");
  const [busy, setBusy] = React.useState(false);

  React.useEffect(() => {
    if (!loading && user) window.location.href = "/dashboard";
  }, [user, loading]);

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const handleGoogle = () => {
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href =
      "https://auth.emergentagent.com/?redirect=" + encodeURIComponent(redirectUrl);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      const path = mode === "login" ? "/auth/login" : "/auth/register";
      const body = mode === "login"
        ? { email: email.trim().toLowerCase(), password }
        : { email: email.trim().toLowerCase(), password, name: name.trim() || undefined };
      const r = await api.post(path, body);
      // Cross-origin cookie fallback: save the session token so subsequent
      // requests can send Authorization: Bearer if the cookie was blocked.
      const tok = r?.data?.session_token;
      if (tok) setStoredToken(tok);
      await checkAuth();
      window.location.href = "/dashboard";
    } catch (e2) {
      setErr(e2?.response?.data?.detail || "Something went wrong");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen editorial-canvas relative overflow-hidden">
      {/* Top nav brand */}
      <header className="absolute top-0 left-0 right-0 flex items-center justify-between px-10 py-6">
        <LinkedInLabBrand variant="full" size="md" mood="cheer" />
      </header>

      <div className="absolute inset-0 flex items-center justify-center px-6 py-10">
        <div className="w-full max-w-md">
          <div className="text-center mb-10">
            <Owlbert mood="cheer" size="xl" float className="mx-auto mb-6" />
            <div className="chapter-eyebrow mb-3">LinkedIn Lab</div>
            <h1 className="chapter-title" style={{ fontSize: 44 }}>
              LinkedIn content,<br />
              <span style={{ color: "#9ca3af" }}>run for you.</span>
            </h1>
            <p className="chapter-sub mt-4 mx-auto">
              Drafts in your voice, engagement comments, and reports for every client — all in one place.
            </p>
          </div>

          <div
            className="ed-card"
            style={{ padding: 28 }}
            data-testid="login-card"
          >
            <div className="flex items-center justify-between mb-4">
              <div className="stat-label">{mode === "login" ? "Sign in" : "Create account"}</div>
              <button
                type="button"
                onClick={() => { setMode(mode === "login" ? "register" : "login"); setErr(""); }}
                className="text-xs text-[#4b5563] hover:text-[#111827] underline underline-offset-4"
                data-testid="toggle-mode-btn"
              >
                {mode === "login" ? "need an account?" : "have an account?"}
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-3">
              {mode === "register" && (
                <input
                  type="text"
                  placeholder="Your name (optional)"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full px-4 py-3 bg-[#fafafa] rounded-xl text-sm text-[#111827] placeholder-[#9ca3af] focus:outline-none focus:ring-2 focus:ring-[#4f46e5]/30"
                  data-testid="register-name-input"
                />
              )}
              <input
                type="email"
                placeholder="Email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-4 py-3 bg-[#fafafa] rounded-xl text-sm text-[#111827] placeholder-[#9ca3af] focus:outline-none focus:ring-2 focus:ring-[#4f46e5]/30"
                data-testid="login-email-input"
              />
              <input
                type="password"
                placeholder="Password"
                required
                minLength={mode === "register" ? 8 : 1}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-4 py-3 bg-[#fafafa] rounded-xl text-sm text-[#111827] placeholder-[#9ca3af] focus:outline-none focus:ring-2 focus:ring-[#4f46e5]/30"
                data-testid="login-password-input"
              />
              {err && (
                <div className="text-xs text-[#ef4444] px-1" data-testid="login-error">{err}</div>
              )}
              <button
                type="submit"
                disabled={busy}
                className="w-full btn-primary py-3 disabled:opacity-50"
                data-testid="login-submit-btn"
              >
                {busy ? "working…" : mode === "login" ? "Sign in" : "Create account"}
              </button>
            </form>

            <div className="flex items-center gap-3 my-5">
              <div className="flex-1 h-px bg-[#e5e7eb]" />
              <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#9ca3af]">or</div>
              <div className="flex-1 h-px bg-[#e5e7eb]" />
            </div>

            <button
              data-testid="google-signin-btn"
              onClick={handleGoogle}
              className="w-full flex items-center justify-center gap-2.5 py-3 rounded-xl bg-white text-[#111827] text-sm font-semibold hover:bg-[#f9fafb] transition-colors"
              style={{ boxShadow: "0 1px 2px rgba(17,24,39,0.06), inset 0 0 0 1px #e5e7eb" }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden>
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.99.66-2.25 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A10.99 10.99 0 0 0 12 23z" />
                <path fill="#FBBC05" d="M5.84 14.09a6.6 6.6 0 0 1 0-4.18V7.07H2.18a11 11 0 0 0 0 9.86l3.66-2.84z" />
                <path fill="#EA4335" d="M12 4.75c1.62 0 3.07.56 4.21 1.64l3.15-3.15C17.45 1.19 14.97.25 12 .25 7.7.25 3.99 2.71 2.18 6.31l3.66 2.84C6.71 6.68 9.14 4.75 12 4.75z" />
              </svg>
              Continue with Google
            </button>
          </div>

          <div className="text-center mt-8 text-[11px] text-[#9ca3af]">
            © LinkedIn Lab · Built by Angad &amp; MB
          </div>
        </div>
      </div>
    </div>
  );
}
